import React from 'react'
import { cm } from '~/utils/utils'
import { Tag, type TagVariant } from './Tag'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Icon element to display */
  icon?: React.ReactNode
  /** Card title */
  title: string
  /** Optional tag to display (e.g., "CONNECTED") */
  tag?: {
    text: string
    variant?: TagVariant
  }
  /** Action button or content on the right side */
  action?: React.ReactNode
  /** Whether the card is in pressed state */
  isPressed?: boolean
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ icon, title, tag, action, isPressed = false, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cm(
          // Base styles
          'w-[400px] h-[80px] p-6 rounded-2xl border border-[#EAEAEA] bg-[#FCFCFC]',
          'hover:bg-[#F5F5F5] transition-colors duration-200 ease-in-out cursor-pointer',
          // Pressed state (only when no tag is present)
          isPressed && !tag && '!bg-[#EAEAEA]',
          className
        )}
        {...props}
      >
        <div className='flex items-center justify-between w-full h-8'>
          {/* Left side: Icon, title and tag */}
          <div className='flex items-center gap-3 flex-1'>
            {/* Icon */}
            {icon && <div className='w-8 h-8 flex items-center justify-center'>{icon}</div>}

            {/* Title */}
            <span className='text-[15px] font-semibold text-[rgba(46,31,54,0.90)] leading-normal font-mono'>
              {title}
            </span>

            {/* Tag */}
            {tag && (
              <Tag variant={tag.variant || 'success'} className='w-auto h-4'>
                {tag.text}
              </Tag>
            )}
          </div>

          {/* Right side: Action */}
          {action && <div className='flex items-center'>{action}</div>}
        </div>
      </div>
    )
  }
)

Card.displayName = 'Card'

export default Card
